import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Phone, PhoneOff, User, Loader2, Clock } from 'lucide-react';

/**
 * Active Call Popup - Shows call status, elapsed time, and disconnect button
 */
export default function ActiveCallPopup({
  open,
  phoneNumber,
  leadName,
  leadCompany,
  callStatus = 'calling', // 'calling', 'connected', 'ended'
  onDisconnect,
  onClose,
  onLogCall,  // Optional: callback to open log call dialog with duration
  elapsedSeconds // Optional: external elapsed time (for syncing)
}) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Start timer when popup opens, continue through all statuses except 'ended'
  useEffect(() => {
    if (open && callStatus !== 'ended') {
      // Start timer if not already running
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
        setElapsedTime(0);
      }
      
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Start the timer interval
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
    } else if (callStatus === 'ended') {
      // Stop timer when call ends
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, callStatus]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      setElapsedTime(0);
      startTimeRef.current = null;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [open]);

  // Format elapsed time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusDisplay = () => {
    switch (callStatus) {
      case 'calling':
        return { text: 'Calling...', color: 'text-amber-600', bg: 'bg-amber-100' };
      case 'connected':
        return { text: 'Connected', color: 'text-green-600', bg: 'bg-green-100' };
      case 'ended':
        return { text: 'Call Ended', color: 'text-slate-600', bg: 'bg-slate-100' };
      default:
        return { text: 'Unknown', color: 'text-slate-600', bg: 'bg-slate-100' };
    }
  };

  if (!open) return null;

  const status = getStatusDisplay();

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <Card className="w-80 shadow-2xl border-2 border-green-200 bg-white">
        <CardContent className="p-4">
          {/* Header with pulse animation for calling state */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`relative w-12 h-12 rounded-full flex items-center justify-center ${status.bg}`}>
              {callStatus === 'calling' && (
                <div className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-25"></div>
              )}
              <Phone className={`h-6 w-6 ${status.color} ${callStatus === 'calling' ? 'animate-pulse' : ''}`} />
            </div>
            <div className="flex-1">
              <p className={`font-semibold ${status.color}`}>{status.text}</p>
              <p className="text-sm text-slate-500">Outbound Call</p>
            </div>
          </div>

          {/* Lead Info */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-800 truncate">{leadName || 'Unknown'}</p>
              {leadCompany && <p className="text-sm text-slate-500 truncate">{leadCompany}</p>}
              <p className="text-sm text-slate-600 font-mono">{phoneNumber}</p>
            </div>
          </div>

          {/* Elapsed Time */}
          <div className="flex items-center justify-center gap-2 mb-4 py-3 bg-slate-100 rounded-lg">
            <Clock className="h-5 w-5 text-slate-600" />
            <span className="text-2xl font-mono font-bold text-slate-800">{formatTime(elapsedTime)}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            {callStatus !== 'ended' ? (
              <>
                <Button 
                  variant="destructive" 
                  className="w-full bg-red-600 hover:bg-red-700"
                  onClick={() => onDisconnect(elapsedTime)}
                  data-testid="disconnect-call-btn"
                >
                  <PhoneOff className="h-4 w-4 mr-2" />
                  End Call
                </Button>
                {onLogCall && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => onLogCall(elapsedTime)}
                    data-testid="log-call-btn"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Log Call Details
                  </Button>
                )}
              </>
            ) : (
              <div className="flex gap-2">
                {onLogCall && (
                  <Button 
                    variant="default"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => onLogCall(elapsedTime)}
                    data-testid="log-call-after-btn"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Log Call
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={onClose}
                  data-testid="close-call-popup-btn"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
